import React, { Component } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { getPosAppVersion, stationShortNumberDivider } from '../../constants/stations';
import { stationModels } from '../../constants/stationModels';

const styles = {
    menu: base => ({
        ...base,
        width: 'max-content',
        minWidth: '100%',
    }),
};
const SEARCH_DEBOUNCE_DELAY = 500;
const selectWidth = 'min(250px, calc(100vw - 150px))';

const selectStyles = {
    ...styles,
    control: css => ({
        ...css,
        width: selectWidth,
    }),
    menu: css => ({
        ...css,
        width: selectWidth,
    }),
    container: css => ({
        ...css,
        marginTop: '5px',
        fontSize: '0.875rem',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    }),
};

const optionLabelStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
};
const modelBadgeStyles = {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    backgroundColor: '#E0E0E0',
    color: '#757575',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 1,
    flexShrink: 0,
};
const getStatusDotStyles = isOnline => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: isOnline ? '#2e7d32' : '#d32f2f',
    boxShadow: isOnline ? '0 0 0 2px rgba(46, 125, 50, 0.16)' : '0 0 0 2px rgba(211, 47, 47, 0.16)',
    flexShrink: 0,
});

const getStationModelLetter = model => {
    switch (model) {
        case stationModels.relink_station:
            return 'R';
        case stationModels.dudu_station:
            return 'D';
        default:
            return 'M';
    }
};

const renderStationOptionLabel = (option, label) => (
    <span style={optionLabelStyles}>
        <span style={modelBadgeStyles}>{getStationModelLetter(option.model)}</span>
        <span>{label}</span>
        <span
            style={getStatusDotStyles(option.status === 'online')}
            title={option.status === 'online' ? 'Онлайн' : 'Оффлайн'}
        />
    </span>
);

class StationSwitcher extends Component {
    state = {
        snoInputValue: '',
        shortNumberInputValue: '',
    };

    componentWillUnmount() {
        this.clearSearchTimeout();
    }

    clearSearchTimeout = () => {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = null;
        }
    };

    resetSearchInputs = () => {
        this.clearSearchTimeout();
        this.setState(prevState =>
            prevState.snoInputValue || prevState.shortNumberInputValue
                ? {
                      snoInputValue: '',
                      shortNumberInputValue: '',
                  }
                : null
        );
    };

    getSearchParams = (searchField, inputValue) => {
        const trimmedValue = inputValue.trim();

        if (!trimmedValue) {
            return {
                searchField: null,
                searchQuery: '',
                rawSearchQuery: '',
                searchPrefix: '',
            };
        }

        if (searchField === 'holder_short_number') {
            const [prefixPart, ...shortNumberParts] = trimmedValue
                .split(stationShortNumberDivider)
                .map(part => part.trim());

            if (prefixPart && shortNumberParts.length && /^\d+$/.test(shortNumberParts.join(''))) {
                const normalizedShortNumber = shortNumberParts
                    .join(stationShortNumberDivider)
                    .trim();

                return {
                    searchField,
                    searchQuery: normalizedShortNumber,
                    rawSearchQuery: trimmedValue,
                    searchPrefix: prefixPart,
                };
            }

            if (/^\d+$/.test(trimmedValue)) {
                return {
                    searchField,
                    searchQuery: trimmedValue,
                    rawSearchQuery: trimmedValue,
                    searchPrefix: '',
                };
            }

            const normalizedPrefix = trimmedValue
                .split(stationShortNumberDivider)[0]
                .replace(/\d+/g, '')
                .trim();

            return {
                searchField: 'holder_prefix',
                searchQuery: normalizedPrefix || trimmedValue,
                rawSearchQuery: normalizedPrefix || trimmedValue,
                searchPrefix: '',
            };
        }

        return {
            searchField,
            searchQuery: trimmedValue,
            rawSearchQuery: trimmedValue,
            searchPrefix: '',
        };
    };

    scheduleSearch = (searchField, inputValue) => {
        const { limit, loadStations } = this.props;
        const searchParams = this.getSearchParams(searchField, inputValue);

        this.clearSearchTimeout();
        this.searchTimeout = setTimeout(() => {
            loadStations({
                page: 1,
                limit,
                append: false,
                ...searchParams,
            });
        }, SEARCH_DEBOUNCE_DELAY);
    };

    handleInputChange = searchField => (inputValue, meta) => {
        if (meta.action === 'input-blur' || meta.action === 'menu-close') {
            this.resetSearchInputs();
            return '';
        }

        if (meta.action !== 'input-change') {
            return inputValue;
        }

        const isShortNumberSearch = searchField === 'holder_short_number';

        this.setState({
            snoInputValue: isShortNumberSearch ? '' : inputValue,
            shortNumberInputValue: isShortNumberSearch ? inputValue : '',
        });
        this.scheduleSearch(searchField, inputValue);

        return inputValue;
    };

    handleSelectChange = getSelectedValue => value => {
        if (!value) {
            return;
        }

        this.clearSearchTimeout();
        this.setState({
            snoInputValue: '',
            shortNumberInputValue: '',
        });
        this.props.choice(getSelectedValue(value));
    };

    handleSelectBlur = () => {
        this.resetSearchInputs();
    };

    handleSelectKeyDown = event => {
        if (event.key === 'Escape') {
            this.resetSearchInputs();
        }
    };

    handleMenuScrollToBottom = () => {
        const { total, snoList, loadedTo, loadMore, isLoading, limit } = this.props;
        const currentTo = loadedTo || snoList.length;

        if (!isLoading && total > currentTo) {
            loadMore({ offset: currentTo, limit });
        }
    };

    render() {
        const {
            snoList,
            active,
            activeStation,
            total,
            displayTotal,
            loadedTo,
            isLoading,
            showPosAppInfo,
        } = this.props;
        const { snoInputValue, shortNumberInputValue } = this.state;
        const isSnoSearching = !!snoInputValue;
        const isShortNumberSearching = !!shortNumberInputValue;
        const shortNumberBySno = new Map();
        const snoByShortNumber = new Map();
        const stationOptions = [];
        const stationShortNumberOptions = [];
        const loaded = snoList.length;
        const shownFrom = loaded ? 1 : 0;
        const shownTotal = displayTotal || total || loaded;
        const shownTo = total ? Math.min(loadedTo || loaded, total) : loadedTo || loaded;
        const hasMore = total > shownTo;
        const loadingMessage = hasMore ? 'Загружаем станции...' : 'Все станции загружены';
        const selectedStation = activeStation || snoList.find(station => station.sno === active);
        if (snoList.length > 0) {
            snoList.forEach(stationData => {
                const short_number =
                    stationData.holder_prefix && stationData.holder_short_number
                        ? `${stationData.holder_prefix}${stationShortNumberDivider}${stationData.holder_short_number}`
                        : stationData.holder_short_number;

                if (stationData.holder_short_number) {
                    snoByShortNumber.set(short_number, stationData.sno);
                }

                const station = {
                    ...stationData,
                    short_number,
                };

                if (stationData.holder_short_number) {
                    stationShortNumberOptions.push(station);
                }

                stationOptions.push(station);
                shortNumberBySno.set(stationData.sno, short_number);
            });
        }
        const activeShortNumber =
            selectedStation && selectedStation.holder_short_number
                ? selectedStation.holder_prefix && selectedStation.holder_short_number
                    ? `${selectedStation.holder_prefix}${stationShortNumberDivider}${selectedStation.holder_short_number}`
                    : selectedStation.holder_short_number
                : shortNumberBySno.get(active);
        const activeSnoOption =
            !isShortNumberSearching && selectedStation
                ? {
                      ...selectedStation,
                      short_number: activeShortNumber,
                  }
                : null;
        const activeShortNumberOption =
            !isSnoSearching && active
                ? {
                      ...(selectedStation || {}),
                      short_number: activeShortNumber || 'Нет',
                  }
                : null;
        const posAppVersion =
            selectedStation && showPosAppInfo ? getPosAppVersion(selectedStation.posapp_version) : '';
        const stationInfoRows = [];
        if (posAppVersion) {
            stationInfoRows.push({
                key: 'posapp',
                label: 'Версия POSApp',
                value: posAppVersion,
            });
        }
        return (
            <div className="station_switcher">
                <div className="station_switcher-fields">
                    <div>
                        <label
                            className="station-control-label"
                            id="station_sno-aria-label"
                            htmlFor="station_sno"
                        >
                            SNO станции
                        </label>
                        <Select
                            styles={selectStyles}
                            aria-labelledby="station_sno-aria-label"
                            inputId="station_sno"
                            options={stationOptions}
                            value={activeSnoOption}
                            onChange={this.handleSelectChange(value => value.sno)}
                            inputValue={snoInputValue}
                            onInputChange={this.handleInputChange('sno')}
                            onBlur={this.handleSelectBlur}
                            onKeyDown={this.handleSelectKeyDown}
                            formatOptionLabel={option =>
                                renderStationOptionLabel(option, option.sno)
                            }
                            getOptionLabel={option => option.sno}
                            getOptionValue={option => option.sno}
                            menuShouldScrollIntoView={true}
                            placeholder="..."
                            isLoading={isLoading}
                            filterOption={() => true}
                            onMenuScrollToBottom={this.handleMenuScrollToBottom}
                            loadingMessage={() => loadingMessage}
                            noOptionsMessage={() =>
                                isLoading ? 'Загружаем станции...' : 'Станции не найдены'
                            }
                        />
                    </div>
                    <div>
                        <label
                            className="station-control-label"
                            id="station_short_number-aria-label"
                            htmlFor="station_short_number"
                        >
                            Код станции
                        </label>
                        <Select
                            styles={selectStyles}
                            aria-labelledby="station_short_number-aria-label"
                            inputId="station_short_number"
                            options={stationShortNumberOptions}
                            value={activeShortNumberOption}
                            onChange={this.handleSelectChange(value =>
                                snoByShortNumber.get(value.short_number)
                            )}
                            inputValue={shortNumberInputValue}
                            onInputChange={this.handleInputChange('holder_short_number')}
                            onBlur={this.handleSelectBlur}
                            onKeyDown={this.handleSelectKeyDown}
                            formatOptionLabel={option =>
                                renderStationOptionLabel(option, option.short_number)
                            }
                            getOptionLabel={option => option.short_number}
                            getOptionValue={option => option.short_number}
                            menuShouldScrollIntoView={true}
                            placeholder="..."
                            isLoading={isLoading}
                            filterOption={() => true}
                            onMenuScrollToBottom={this.handleMenuScrollToBottom}
                            loadingMessage={() => loadingMessage}
                            noOptionsMessage={() =>
                                isLoading ? 'Загружаем станции...' : 'Станции не найдены'
                            }
                        />
                    </div>
                </div>
                <div className="station_switcher-count">
                    Показано с {shownFrom} по {shownTo} из {shownTotal}
                </div>
                {selectedStation && showPosAppInfo && (
                    <div className="station_switcher-station-info">
                        {stationInfoRows.map(row => (
                            <div
                                key={row.key}
                                className="station_switcher-station-info-row"
                            >
                                <span className="station_switcher-station-info-key">
                                    {row.label}
                                </span>
                                <span className="station_switcher-station-info-value">
                                    {row.value || null}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

StationSwitcher.propTypes = {
    snoList: PropTypes.array,
    choice: PropTypes.func,
    active: PropTypes.string,
    activeStation: PropTypes.object,
    total: PropTypes.number,
    displayTotal: PropTypes.number,
    loadedTo: PropTypes.number,
    limit: PropTypes.number,
    loadStations: PropTypes.func,
    loadMore: PropTypes.func,
    isLoading: PropTypes.bool,
    showPosAppInfo: PropTypes.bool,
};

export default StationSwitcher;
